import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { EXPERIENCE } from '../../constants';
import SectionHeading from '../UI/SectionHeading';

interface ExperienceProps {
  isDetailed?: boolean;
}

const Experience: React.FC<ExperienceProps> = ({ isDetailed = false }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section id='experience' className='scroll-mt-20 max-w-7xl mx-auto px-5 sm:px-6'>
      {!isDetailed && (
        <SectionHeading
          index='05'
          kicker='Experience'
          title='Where the work happened'
          description='From finance internships to running transformation workstreams inside a bank.'
        />
      )}

      <div className='border-t border-line'>
        {EXPERIENCE.map((exp, index) => {
          const isExpanded = expandedId === exp.id || isDetailed;

          return (
            <motion.div
              key={exp.id}
              initial={isDetailed ? false : { opacity: 0, y: 24 }}
              animate={isDetailed ? { opacity: 1, y: 0 } : undefined}
              whileInView={isDetailed ? undefined : { opacity: 1, y: 0 }}
              viewport={isDetailed ? undefined : { once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: index * 0.07, ease: [0.16, 1, 0.3, 1] }}
              className='border-b border-line'
            >
              {/* Row header */}
              <button
                onClick={() => !isDetailed && toggleExpand(exp.id)}
                data-cursor='pointer'
                className='group w-full grid grid-cols-[1fr_auto] sm:grid-cols-[180px_1fr_auto] gap-x-6 gap-y-1 items-baseline text-left py-7 sm:py-8'
              >
                <span className='label-mono text-gray2 hidden sm:block'>{exp.period}</span>
                <span>
                  <span className='label-mono text-gray2 sm:hidden block mb-1.5'>{exp.period}</span>
                  <span className='font-medium tracking-tight text-xl sm:text-2xl text-ink group-hover:translate-x-1.5 inline-block transition-transform duration-400'>
                    {exp.company}
                  </span>
                  <span className='block mt-1 text-sm text-gray1'>
                    {exp.role}, {exp.location}
                  </span>
                </span>
                {!isDetailed && (
                  <span
                    className={`w-10 h-10 self-center rounded-full border border-line flex items-center justify-center text-ink transition-all duration-400 group-hover:border-ink ${
                      isExpanded ? 'rotate-45 bg-ink text-paper border-ink' : ''
                    }`}
                  >
                    <Plus size={15} />
                  </span>
                )}
              </button>

              {/* Expanded details */}
              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className='overflow-hidden'
                  >
                    <div className='pb-9 sm:grid sm:grid-cols-[180px_1fr] sm:gap-6'>
                      <div className='hidden sm:block' />
                      <div className='max-w-3xl'>
                        <ul className='space-y-3.5'>
                          {exp.highlights.map((highlight, i) => (
                            <motion.li
                              key={i}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.06 }}
                              className='flex items-start gap-3.5 text-sm leading-relaxed text-gray1'
                            >
                              <span className='mt-2 h-1 w-3 bg-accent shrink-0' />
                              {highlight}
                            </motion.li>
                          ))}
                        </ul>

                        <div className='flex flex-wrap gap-2 mt-7'>
                          {exp.skills.map(skill => (
                            <span
                              key={skill}
                              className='label-mono !text-[10px] px-3 py-1.5 rounded-full bg-surface text-gray1 hover:text-ink transition-colors duration-300'
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

export default Experience;
